<template>

    <tr
        class="select-none odd:bg-gray-100">
        <td class="pl-6 max-w-0" @click.prevent="selectAsset" v-if="!showMergeOption">
            <checkbox :value="isChecked" style="z-index: 0 !important;"></checkbox>
        </td>
        <!-- MOBILE STYLING -->
        <td class="w-full max-w-0 py-2 py-4 px-4 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6 cursor-pointer"
            @click.prevent="navigateToAsset('window')">
            <div class="text-lg sm:text-sm">
                <div class="block cursor-pointer flex items-start">
                    <div>
                        <!--On small screens we don't want to open a second tab-->
                        <span class="inline-block md:hidden" @click.stop="navigateToAsset('window')">{{ asset.name }}</span>
                        <span class="hidden md:inline-block" @click.stop="navigateToAsset('blank')">{{ asset.name }}</span>
                        <el-tooltip content="Managed Asset" placement="top">
                            <span v-if="asset.current_mandate_type === 'managed'"
                                  class="bg-indigo-100 border border-indigo-200 text-indigo-600 rounded-md text-sm px-1 mx-1">M</span>
                        </el-tooltip>
                        <el-tooltip content="Unmanaged Asset" placement="top">
                            <span v-if="asset.current_mandate_type === 'unmanaged'"
                                  class="bg-orange-100 border border-orange-200 text-orange-600 rounded-md text-sm px-1 mx-1">U</span>
                        </el-tooltip>
                    </div>
                    <div v-if="asset.external_source" class="text-2xs flex items-center -mt-1 font-normal"
                         :class="[asset.current_mandate_type === 'unmanaged' ? 'text-orange-600' : 'text-indigo-600']">
                        <icon-link class="h-3 w-3 mx-1"></icon-link>
                        <span>{{ asset.external_source.integration_alias }}</span>
                    </div>
                </div>
                <div class="hidden md:block lg:line-clamp-1 text-gray-400 font-normal" style="max-width: 300px;">
                    {{ asset.full_address }}
                </div>
                <div class="hidden md:block text-gray-400 font-normal" style="max-width: 300px;">
                    <span class="text-sm text-indigo-600 font-normal mr-1">{{ asset.portfolio_name }}</span>
                </div>
            </div>

            <dl class="font-normal lg:hidden text-lg sm:text-sm py-2 md:py-4 leading-none">
                <dd class="mt-1 text-gray-900" v-if="asset.primary_landlord">{{ asset.primary_landlord.full_name }}</dd>
                <dd class="mt-1 text-indigo-600" v-if="asset.primary_tenant">{{ asset.primary_tenant.full_name }}</dd>
            </dl>
        </td>

        <!-- DESKTOP STYLING -->
        <td class="hidden px-3 py-4 text-sm text-gray-900 font-medium lg:table-cell">
            <div>
                <dd class="text-gray-900" v-if="asset.primary_landlord">{{ asset.primary_landlord.full_name }}</dd>
                <dd class="text-indigo-600" v-if="asset.primary_tenant">{{ asset.primary_tenant.full_name }}</dd>
            </div>
        </td>

        <td
            class="hidden px-3 py-4 text-sm text-gray-900 font-medium lg:table-cell text-center">
            <div v-if="asset.last_inspection" @click.prevent="openInspectionInNewTab(asset.last_inspection.id)" :title="asset.last_inspection.date"
                 class="cursor-pointer px-2 py-1 border border-green-400 text-green-600 bg-green-100 hover:text-green-800 hover:bg-green-200 transition duration-300 rounded-lg inline-block">
                <div class="block">{{ asset.last_inspection.type }} Inspection</div>
                <div class="block font-normal">{{ asset.last_inspection.date_relative }}</div>
            </div>
            <div class="-mt-1" v-else>
                <div class="text-gray-400 font-normal">Never</div>
            </div>
        </td>

        <td class="hidden px-3 py-4 text-sm text-gray-900 font-medium lg:table-cell text-center">
            <div v-if="asset.next_inspection" @click.prevent="openInspectionInNewTab(asset.next_inspection.id)" :title="asset.next_inspection.date"
                 class="cursor-pointer px-2 py-1 border border-indigo-200 bg-indigo-100 rounded-lg text-indigo-600 hover:text-indigo-800 hover:bg-indigo-200 transition duration-300 inline-block">
                <div class="block">{{ asset.next_inspection.type }} Inspection</div>
                <div class="block font-normal">{{ asset.next_inspection.date_relative }}</div>
            </div>
            <div class="text-gray-400 font-normal" v-else>
                None
            </div>
        </td>

        <td class="hidden px-3 py-4 text-sm text-gray-900 hover:text-gray-700 font-medium text-center lg:table-cell lg:border-l lg:border-r text-center border-gray-200">
            <div v-if="asset.next_todo" class="flex items-center justify-center"
                 :class="getTodoColorClass(asset.next_todo)"
                 @click.prevent="handleEditTodoClick">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 mr-2">
                    <path stroke-linecap="round" stroke-linejoin="round"
                          d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"/>
                </svg>
                <span class="whitespace-nowrap cursor-pointer">
                    {{ asset.next_todo.reminder_date }}
                </span>
            </div>

            <button v-else type="button" class="whitespace-nowrap w-full block flex items-center justify-center text-gray-400 hover:text-gray-700 font-medium"
                    @click.prevent="handleCreateTodoClick">
                <icon-plus-circle class="h-4 w-4 mr-2"></icon-plus-circle>
                <span>Create To-do</span>
            </button>
        </td>


        <td class="hidden md:table-cell py-4 px-3 text-right text-sm font-medium md:text-center">
            <button v-if="showMergeOption" @click.prevent="emitMergeEvent"
                    class="font-bold text-brand hover:text-red-500 hover:bg-red-100 px-2 py-1 rounded-lg">Merge
            </button>
            <a v-else :href="`/assets/${asset.id}`" class="font-medium text-indigo-600 hover:text-indigo-500">View</a>
        </td>
    </tr>

</template>

<script>
import { mapState } from "vuex";

export default {
    name: "AssetRow",
    props: ["asset", "checked"],
    data () {
        return {
            isChecked: this.checked,
        };
    },
    watch: {
        checked (val) {
            this.isChecked = val;
        },
    },
    computed: {
        ...mapState({
            showMergeOption: state => state.assetList.showMergeOption,
        }),
    },
    methods: {
        selectAsset () {
            this.$emit("toggle-row-selection", this.asset.id);
        },
        navigateToAsset (target) {
            if (target && target == "blank") {
                window.open("/assets/" + this.asset.id, "_blank");
            } else {
                window.location.href = "/assets/" + this.asset.id;
            }
        },
        handleEditTodoClick () {
            this.$emit("edit-todo", this.asset);
        },
        handleCreateTodoClick () {
            this.$emit("create-todo", this.asset);
        },
        openInspectionInNewTab (inspectionId) {
            window.open("/inspections/" + inspectionId, "_blank");
        },
        emitMergeEvent () {
            this.$emit("merge-asset", this.asset);
        },
        getTodoColorClass (todo) {
            const today = this.$dayjs().startOf("day");
            const reminderDate = this.$dayjs(todo.reminder_date).startOf("day");

            if (reminderDate.isBefore(today)) {
                return "text-red-600"; // Past date
            } else if (reminderDate.isSame(today)) {
                return "text-yellow-400"; // Today
            } else {
                return "text-indigo-600"; // Future date
            }
        }
    }
};
</script>

<style scoped>

</style>
